import { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import DistributionPanel from '../components/DistributionPanel';
import { usePipeline } from '../hooks/usePipeline';
import type { GeneratedBundle } from '../services/openai';
import { mockDistributionPlatforms } from '../data/index';
import {
  publishToAllPlatforms,
  loadPlatformConfigs,
  savePlatformConfigs,
  type PlatformId,
  type PlatformConfigs,
  type PublishResult,
  DEFAULT_PLATFORM_CONFIGS,
} from '../services/publisher';
import {
  verifyLinkedInToken,
  publishToLinkedIn,
  saveLinkedInProfile,
  clearLinkedInProfile,
  getLinkedInProfile,
  type LinkedInProfile,
  type LinkedInPostResult,
} from '../services/linkedin';

const Distribution = () => {
  const {
    triggerDistribution,
    triggerAllDistributions,
    distributionStatus,
    isDistributing,
  } = usePipeline();

  // ── LinkedIn Direct Token state ───────────────────────────────────────────
  const [linkedInProfile, setLinkedInProfile]   = useState<LinkedInProfile | null>(getLinkedInProfile);
  const [liTokenInput, setLiTokenInput]         = useState('');
  const [liUrnInput, setLiUrnInput]             = useState('');
  const [liVerifying, setLiVerifying]           = useState(false);
  const [liVerifyWarn, setLiVerifyWarn]         = useState('');
  const [linkedInPosting, setLinkedInPosting]   = useState(false);
  const [linkedInResult, setLinkedInResult]     = useState<LinkedInPostResult | null>(null);
  const [linkedInError, setLinkedInError]       = useState('');
  const [liShowToken, setLiShowToken]           = useState(false);
  const [liPostText, setLiPostText]             = useState('');
  const [liCopied, setLiCopied]                 = useState(false);
  const liTokenRef = useRef<HTMLTextAreaElement>(null);

  // ── Pre-populate post text from latest bundle ───────────────────────────────
  useEffect(() => {
    const b = getLatestApprovedBundle();
    if (b?.blogPost) {
      const title   = b.blogPost.title || b.trendName || '';
      const excerpt = b.blogPost.excerpt || '';
      const tags    = (b.blogPost.hashtags || []).map((h: string) => h.startsWith('#') ? h : `#${h}`).join(' ');
      setLiPostText(`${title}\n\n${excerpt}${tags ? '\n\n' + tags : ''}`.trim());
    }
  }, []);

  // ── Token verify & connect ────────────────────────────────────────────────
  const handleLinkedInConnect = async () => {
    const token = liTokenInput.trim();
    if (!token) { setLinkedInError('Please paste your LinkedIn Access Token first.'); return; }
    setLiVerifying(true);
    setLinkedInError('');
    setLiVerifyWarn('');
    const result = await verifyLinkedInToken(token);
    setLiVerifying(false);
    if (!result.ok && !result.personUrn) {
      setLinkedInError(result.error || 'Verification failed.');
      return;
    }
    if (result.error?.startsWith('CORS_WARN')) {
      setLiVerifyWarn('Profile could not be auto-fetched (CORS). Token saved. Enter your Author URN below.');
    }
    const profile: LinkedInProfile = {
      accessToken:  token,
      personUrn:    liUrnInput.trim() || result.personUrn || '',
      name:         result.name || 'LinkedIn User',
      email:        result.email,
      picture:      result.picture,
      connectedAt:  new Date().toISOString(),
    };
    saveLinkedInProfile(profile);
    setLinkedInProfile(profile);
    setLiTokenInput('');
  };

  const handleLinkedInDisconnect = () => {
    clearLinkedInProfile();
    setLinkedInProfile(null);
    setLinkedInResult(null);
    setLiTokenInput('');
    setLiUrnInput('');
  };

  const handleLinkedInPublish = async () => {
    if (!linkedInProfile) return;
    setLinkedInPosting(true);
    setLinkedInResult(null);
    setLinkedInError('');
    const b = getLatestApprovedBundle();
    try {
      const result = await publishToLinkedIn({
        accessToken: linkedInProfile.accessToken,
        personUrn:   linkedInProfile.personUrn,
        title:       b?.blogPost?.title || 'New Post',
        caption:     '',
        excerpt:     liPostText || b?.blogPost?.excerpt || '',
        hashtags:    b?.blogPost?.hashtags || [],
        postType:    'feed_post',
      });
      setLinkedInResult(result);
    } catch (e) {
      setLinkedInResult({ success: false, error: String(e) });
    }
    setLinkedInPosting(false);
  };

  const handleCopyPost = () => {
    navigator.clipboard.writeText(liPostText).then(() => {
      setLiCopied(true);
      setTimeout(() => setLiCopied(false), 2000);
    });
  };

  const getLatestApprovedBundle = (): GeneratedBundle | null => {
    try {
      const raw = localStorage.getItem('uxcf_generated_bundles');
      const bs: GeneratedBundle[] = raw ? JSON.parse(raw) : [];
      const approved = bs.filter((b) => b.approvalStatus === 'approved');
      return approved[0] || bs[0] || null;
    } catch { return null; }
  };

  const getInfographicImageUrl = (): string | null => {
    try {
      // v2: URLs are stored in uxcf_infographic_urls (plain string map, no base64)
      const raw = localStorage.getItem('uxcf_infographic_urls');
      const map: Record<string, string | null> = raw ? JSON.parse(raw) : {};
      const first = Object.values(map).find((v) => v && typeof v === 'string');
      return first || null;
    } catch { return null; }
  };

  const getComicImageUrl = (): string | null => {
    try {
      // v2: URLs are stored in uxcf_comic_urls (plain string map, no base64)
      const raw = localStorage.getItem('uxcf_comic_urls');
      const map: Record<string, string | null> = raw ? JSON.parse(raw) : {};
      const first = Object.values(map).find((v) => v && typeof v === 'string');
      return first || null;
    } catch { return null; }
  };

  // Approval synced with ContentPipeline via localStorage
  const [approvalStatus, setApprovalStatus] = useState<'pending'|'approved'|'rejected'>(()=>{
    try {
      const raw=localStorage.getItem('uxcf_generated_bundles');
      const bs:any[]=raw?JSON.parse(raw):[];
      if(bs.some((b:any)=>b.approvalStatus==='approved'))return 'approved';
      if(bs.some((b:any)=>b.approvalStatus==='rejected'))return 'rejected';
    } catch {}
    return 'pending';
  });

  const approveLatest=()=>{
    try{
      const raw=localStorage.getItem('uxcf_generated_bundles');
      const bs:any[]=raw?JSON.parse(raw):[];
      if(bs.length>0){bs[0].approvalStatus='approved';localStorage.setItem('uxcf_generated_bundles',JSON.stringify(bs));}
    }catch{}
    setApprovalStatus('approved');
  };

  const [platforms] = useState<any[]>(mockDistributionPlatforms);

  // ── Omnichannel Publishing Hub state ───────────────────────────────────────
  const [platformConfigs, setPlatformConfigs] = useState<PlatformConfigs>(
    () => loadPlatformConfigs()
  );
  const [publishResults, setPublishResults]   = useState<PublishResult[]>([]);
  const [publishingAll, setPublishingAll]     = useState(false);
  const [pubLogs, setPubLogs]                 = useState<{ts:string;level:'info'|'success'|'error';msg:string}[]>([]);

  const addPubLog = (level: 'info'|'success'|'error', msg: string) =>
    setPubLogs(prev => [{ ts: new Date().toLocaleTimeString(), level, msg }, ...prev.slice(0,29)]);

  const updatePlatformConfig = (platform: PlatformId, field: string, value: string | boolean) => {
    setPlatformConfigs(prev => {
      const next = { ...prev, [platform]: { ...prev[platform], [field]: value } };
      savePlatformConfigs(next);
      return next;
    });
  };

  const handlePublishAll = async () => {
    const bundle = getLatestApprovedBundle();
    if (!bundle) { addPubLog('error', 'No generated bundle found. Run the Content Pipeline first.'); return; }
    setPublishingAll(true);
    setPublishResults([]);
    const enabledPlatforms = (Object.keys(platformConfigs) as PlatformId[]).filter(p => platformConfigs[p].enabled);
    if (enabledPlatforms.length === 0) { addPubLog('error', 'Enable at least one platform target.'); setPublishingAll(false); return; }
    addPubLog('info', `Dispatching to ${enabledPlatforms.join(', ')}…`);
    const results = await publishToAllPlatforms(bundle, platformConfigs);
    setPublishResults(results);
    results.forEach(r => addPubLog(r.success ? 'success' : 'error', `[${r.platform}] ${r.message}`));
    setPublishingAll(false);
  };

  const isApproved = approvalStatus === 'approved';

  // ── Headless CMS API Delivery state ──────────────────────────────────────────
  type CMSPlatform = 'strapi' | 'sanity' | 'contentful';
  const [cmsTab, setCmsTab]               = useState<CMSPlatform>('strapi');
  const [cmsConfig, setCmsConfig]         = useState<Record<CMSPlatform, Record<string, string>>>({
    strapi:     { baseUrl: '', token: '', collection: 'articles' },
    sanity:     { projectId: '', dataset: 'production', token: '', docType: 'article' },
    contentful: { spaceId: '', env: 'master', token: '', contentTypeId: 'article' },
  });
  const [cmsPublishing, setCmsPublishing] = useState(false);
  const [cmsLogs, setCmsLogs]             = useState<{ ts: string; level: 'info' | 'success' | 'error'; msg: string }[]>([]);

  const updateCmsCfg = (field: string, value: string) =>
    setCmsConfig(prev => ({ ...prev, [cmsTab]: { ...prev[cmsTab], [field]: value } }));

  const buildStructuredPayload = () => {
    const b = getLatestApprovedBundle();
    const title = b?.blogPost?.title ?? 'Untitled';
    const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
    return {
      entry_meta: { title, slug, content_type: 'article' },
      components: [
        { type: 'hero_section', fields: { heading: title, lead_text: b?.blogPost?.excerpt ?? '' } },
        { type: 'rich_text',    fields: { body_markdown: b?.blogPost?.body ?? '' } },
        ...(b?.blogPost?.hashtags?.length ? [{ type: 'tags_block', fields: { tags: b.blogPost.hashtags } }] : []),
      ],
      seo_payload: {
        meta_title: title,
        meta_description: (b?.blogPost?.excerpt ?? '').slice(0, 155),
        keywords: b?.blogPost?.hashtags ?? [],
      },
    };
  };

  const addLog = (level: 'info' | 'success' | 'error', msg: string) =>
    setCmsLogs(prev => [{ ts: new Date().toLocaleTimeString(), level, msg }, ...prev.slice(0, 19)]);

  const handleCmsPublish = async () => {
    const payload = buildStructuredPayload();
    const cfg = cmsConfig[cmsTab];
    setCmsPublishing(true);
    addLog('info', `Initiating POST to ${cmsTab.toUpperCase()} endpoint…`);
    try {
      let url = '';
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      let body: unknown = payload;
      if (cmsTab === 'strapi') {
        url = `${cfg.baseUrl.replace(/\/$/, '')}/${cfg.collection}`;
        headers['Authorization'] = `Bearer ${cfg.token}`;
        body = { data: payload };
      } else if (cmsTab === 'sanity') {
        url = `https://${cfg.projectId}.api.sanity.io/v2021-06-07/data/mutate/${cfg.dataset}`;
        headers['Authorization'] = `Bearer ${cfg.token}`;
        body = { mutations: [{ create: { _type: cfg.docType, title: payload.entry_meta.title, slug: { current: payload.entry_meta.slug }, body: (payload.components.find((c: Record<string,unknown>) => c.type === 'rich_text') as Record<string,unknown> | undefined)?.fields as string ?? '', seoTitle: payload.seo_payload.meta_title, seoDescription: payload.seo_payload.meta_description } }] };
      } else if (cmsTab === 'contentful') {
        url = `https://api.contentful.com/spaces/${cfg.spaceId}/environments/${cfg.env}/entries`;
        headers['Authorization'] = `Bearer ${cfg.token}`;
        headers['X-Contentful-Content-Type'] = cfg.contentTypeId;
        body = { fields: { title: { 'en-US': payload.entry_meta.title }, slug: { 'en-US': payload.entry_meta.slug }, body: { 'en-US': ((payload.components.find((c: Record<string,unknown>) => c.type === 'rich_text') as Record<string,unknown> | undefined)?.fields as Record<string,string> | undefined)?.body_markdown ?? '' }, metaDescription: { 'en-US': payload.seo_payload.meta_description }, keywords: { 'en-US': payload.seo_payload.keywords } } };
      }
      if (!url) throw new Error('No API Base URL configured. Fill in all required fields.');
      addLog('info', `POST ${url}`);
      const res = await fetch(url, { method: 'POST', headers, body: JSON.stringify(body) });
      if (res.ok) {
        const json = await res.json().catch(() => ({})) as Record<string,unknown>;
        addLog('success', `✓ ${cmsTab.toUpperCase()} accepted — ID: ${(json?.data as Record<string,unknown>)?.id ?? (json?.sys as Record<string,unknown>)?.id ?? 'ok'}`);
      } else {
        const errText = await res.text().catch(() => res.statusText);
        addLog('error', `✗ HTTP ${res.status}: ${errText.slice(0, 200)}`);
      }
    } catch (err: unknown) {
      addLog('error', `✗ ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setCmsPublishing(false);
    }
  };

  return (
    <Layout>
      <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 max-w-6xl mx-auto">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-violet-700 via-purple-700 to-fuchsia-700 rounded-2xl p-4 sm:p-6 text-white shadow-2xl">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-0.5">Publish Command Center</h1>
              <p className="text-violet-200 text-sm">Multi-platform content deployment hub</p>
            </div>
            <div className="flex gap-3">
              {/* PostFast Skill Badge */}
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13 7H7v6h6V7z" />
                    <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-sm">PostFast</span>
                </div>
              </div>
              {/* OpenClaw Framework Badge */}
              <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/30">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold text-sm">OpenClaw</span>
                </div>
              </div>
            </div>
          </div>

          {/* Approval Gate Notice */}
          <div
            className={`mt-6 p-4 rounded-lg border-2 ${
              isApproved
                ? 'bg-green-500/20 border-green-300/50'
                : 'bg-amber-500/20 border-amber-300/50'
            }`}
          >
            <div className="flex items-center gap-3">
              {isApproved ? (
                <svg className="w-6 h-6 text-green-200" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-amber-200" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              )}
              <div>
                <p className="font-semibold text-lg">
                  {isApproved ? 'Approval Gate: CLEARED ✓' : 'Approval Gate: PENDING APPROVAL'}
                </p>
                <p className="text-sm text-white/80">
                  {isApproved
                    ? 'Content approved and ready for distribution'
                    : 'Content requires approval before distribution can begin'}
                </p>
                {!isApproved && (
                  <button onClick={approveLatest} className='mt-3 px-5 py-2 bg-green-500 hover:bg-green-400 text-white font-semibold rounded-lg text-sm transition-colors'>
                    Approve Content for Distribution
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Integration Status Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { name: 'LinkedIn', connected: !!linkedInProfile, icon: '💼', color: 'border-blue-500/30 bg-blue-500/10 text-blue-400' },
            { name: 'WordPress', connected: false, icon: '🌐', color: 'border-slate-600 bg-slate-800/40 text-slate-500' },
            { name: 'X (Twitter)', connected: false, icon: '✨', color: 'border-slate-600 bg-slate-800/40 text-slate-500' },
            { name: 'Meta / IG',   connected: false, icon: 'IG', color: 'border-slate-600 bg-slate-800/40 text-slate-500' },
          ].map(p => (
            <div key={p.name} className={`flex items-center gap-2.5 p-3 rounded-2xl border ${p.color}`}>
              <span className="text-lg">{p.icon}</span>
              <div>
                <p className="text-xs font-semibold">{p.name}</p>
                <p className="text-[10px] opacity-70">{p.connected ? '● Connected' : '○ Not connected'}</p>
              </div>
            </div>
          ))}
        </div>

        {/* LinkedIn Real Publishing Card */}
        <div className="bg-slate-900 rounded-xl shadow-lg border border-slate-700 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
              <svg viewBox="0 0 24 24" className="w-6 h-6 fill-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">LinkedIn — Real Publishing</h2>
              <p className="text-sm text-slate-400">Publish your generated article directly to your LinkedIn profile</p>
            </div>
            {linkedInProfile && (
              <span className="ml-auto px-3 py-1 bg-green-500/20 text-green-400 border border-green-500/30 rounded-full text-xs font-semibold">● Connected</span>
            )}
          </div>

          {linkedInError && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400 space-y-2">
              <p><strong>⚠ LinkedIn Error:</strong> {linkedInError}</p>
              <button onClick={() => setLinkedInError('')} className="text-xs text-red-400 underline">Dismiss</button>
            </div>
          )}

          {/* ── "How to get your token" guide ──────────────────────────── */}
          {!linkedInProfile && (
            <div className="mb-5 rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
              <p className="text-sm font-semibold text-blue-300">How to connect — 3 steps (2 min)</p>
              <ol className="space-y-2 text-xs text-slate-400 list-none">
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">1</span>
                  <span>Go to <a href="https://www.linkedin.com/developers/tools/oauth/token-generator" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline font-medium">LinkedIn Token Generator</a> (free, takes 60 seconds)</span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">2</span>
                  <span>Select scopes: <code className="bg-slate-800 px-1 rounded font-mono">openid</code> <code className="bg-slate-800 px-1 rounded font-mono">profile</code> <code className="bg-slate-800 px-1 rounded font-mono">email</code> <code className="bg-slate-800 px-1 rounded font-mono">w_member_social</code> — then click <strong className="text-white">Request access token</strong></span>
                </li>
                <li className="flex gap-2">
                  <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-[10px] font-bold flex-shrink-0">3</span>
                  <span>Copy the token and paste it below. Token is valid for <strong className="text-white">60 days</strong>.</span>
                </li>
              </ol>
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs">
                <strong>Need a LinkedIn App?</strong> Go to <a href="https://www.linkedin.com/developers/apps/new" target="_blank" rel="noopener noreferrer" className="underline">linkedin.com/developers/apps/new</a> &rarr; create app &rarr; Auth tab &rarr; add OAuth 2.0 scope <code className="bg-amber-500/20 px-1 rounded font-mono">w_member_social</code>
              </div>
            </div>
          )}

          {/* ── Token input form (shown when not connected) ─────────────── */}
          {!linkedInProfile ? (
            <div className="space-y-4">
              {liVerifyWarn && (
                <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs">{liVerifyWarn}</div>
              )}
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-400">Access Token <span className="text-slate-600 font-normal">(from LinkedIn Token Generator)</span></label>
                <div className="relative">
                  <textarea
                    ref={liTokenRef}
                    rows={3}
                    className="w-full bg-slate-800/70 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:outline-none font-mono resize-none pr-16"
                    placeholder="AQV...paste your LinkedIn access token here..."
                    value={liTokenInput}
                    onChange={e => setLiTokenInput(e.target.value)}
                  />
                  <button
                    onClick={() => setLiShowToken(v => !v)}
                    className="absolute top-2 right-2 text-xs text-slate-500 hover:text-slate-300 bg-slate-700 px-2 py-1 rounded"
                  >{liShowToken ? 'Hide' : 'Show'}</button>
                </div>
                <p className="text-[10px] text-slate-600">Treat this like a password — it grants posting access to your LinkedIn account.</p>
              </div>
              <div className="space-y-1">
                <label className="block text-xs font-semibold text-slate-400">
                  Author URN <span className="text-slate-600 font-normal">(optional — auto-detected if blank)</span>
                </label>
                <input
                  className="w-full bg-slate-800/70 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:outline-none font-mono"
                  placeholder="urn:li:person:XXXXXXXX"
                  value={liUrnInput}
                  onChange={e => setLiUrnInput(e.target.value)}
                />
                <p className="text-[10px] text-slate-600">Find via: GET https://api.linkedin.com/v2/me (with your token in Authorization header)</p>
              </div>
              <button
                onClick={handleLinkedInConnect}
                disabled={liVerifying || !liTokenInput.trim()}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                {liVerifying ? (
                  <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Verifying token...</>
                ) : (
                  <><svg viewBox="0 0 24 24" className="w-5 h-5 fill-white"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452z"/></svg> Verify Token &amp; Connect LinkedIn</>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Profile card */}
              <div className="flex items-center gap-4 p-4 bg-slate-800 border border-slate-700 rounded-xl">
                {linkedInProfile.picture ? (
                  <img src={linkedInProfile.picture} alt={linkedInProfile.name} className="w-12 h-12 rounded-full border-2 border-blue-500/40" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-blue-700 flex items-center justify-center text-white font-bold text-lg">
                    {linkedInProfile.name?.charAt(0) || 'L'}
                  </div>
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-white">{linkedInProfile.name}</p>
                    <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] font-bold rounded-full border border-green-500/30">CONNECTED</span>
                  </div>
                  {linkedInProfile.email && <p className="text-sm text-slate-400">{linkedInProfile.email}</p>}
                  {linkedInProfile.personUrn && <p className="text-xs text-slate-600 font-mono">{linkedInProfile.personUrn}</p>}
                  <p className="text-xs text-slate-500 mt-0.5">Connected {new Date(linkedInProfile.connectedAt).toLocaleDateString()}</p>
                </div>
                <button onClick={handleLinkedInDisconnect} className="text-xs text-red-400 hover:text-red-300 underline flex-shrink-0">Disconnect</button>
              </div>

              {/* Missing URN warning */}
              {!linkedInProfile.personUrn && (
                <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs space-y-1">
                  <p className="font-semibold">Author URN required to publish</p>
                  <p>Enter your LinkedIn person URN so posts are attributed to your account:</p>
                  <div className="flex gap-2 mt-2">
                    <input
                      className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-white font-mono text-xs focus:outline-none focus:border-blue-500/50"
                      placeholder="urn:li:person:XXXXXXXX"
                      value={liUrnInput}
                      onChange={e => setLiUrnInput(e.target.value)}
                    />
                    <button
                      onClick={() => {
                        if (!liUrnInput.trim()) return;
                        const updated = { ...linkedInProfile, personUrn: liUrnInput.trim() };
                        saveLinkedInProfile(updated);
                        setLinkedInProfile(updated);
                        setLiUrnInput('');
                      }}
                      className="px-3 py-1.5 bg-amber-600 hover:bg-amber-500 text-white rounded-lg text-xs font-semibold"
                    >Save URN</button>
                  </div>
                  <p className="text-[10px] text-amber-500/70">Tip: run <code className="bg-amber-500/20 px-1 rounded">curl -H &quot;Authorization: Bearer YOUR_TOKEN&quot; https://api.linkedin.com/v2/me</code> to find your URN</p>
                </div>
              )}

              {/* Post composer */}
              <div className="space-y-3">
                <label className="block text-xs font-semibold text-slate-400">Post Content</label>
                <textarea
                  rows={6}
                  className="w-full bg-slate-800/70 border border-slate-700 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-blue-500/50 focus:outline-none resize-y"
                  placeholder="Your post content (auto-filled from generated content)..."
                  value={liPostText}
                  onChange={e => setLiPostText(e.target.value)}
                />
                <p className="text-[10px] text-slate-600">{liPostText.length}/3000 characters</p>

                <div className="flex gap-3">
                  <button
                    onClick={handleLinkedInPublish}
                    disabled={linkedInPosting || !liPostText.trim() || !linkedInProfile.personUrn}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm"
                  >
                    {linkedInPosting ? (
                      <><svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Publishing...</>
                    ) : 'Publish to LinkedIn'}
                  </button>
                  <button
                    onClick={handleCopyPost}
                    className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-slate-200 font-semibold rounded-xl transition-colors text-sm"
                  >
                    {liCopied ? 'Copied!' : 'Copy Post'}
                  </button>
                </div>
                <p className="text-[10px] text-slate-600">
                  Note: If the publish button returns a CORS error, use <strong className="text-slate-400">Copy Post</strong> and paste directly on <a href="https://www.linkedin.com/feed/" target="_blank" rel="noopener noreferrer" className="underline text-blue-400">linkedin.com</a>. CORS restrictions are a LinkedIn API limitation for browser-based apps.
                </p>
              </div>

              {/* Publish result */}
              {linkedInResult && (
                <div className={`p-4 rounded-xl border text-sm ${linkedInResult.success ? 'bg-green-500/10 border-green-500/30 text-green-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
                  {linkedInResult.success ? (
                    <div className="space-y-1">
                      <p className="font-semibold">Post published successfully!</p>
                      {linkedInResult.post_url && <a href={linkedInResult.post_url} target="_blank" rel="noopener noreferrer" className="underline text-blue-400 text-xs">View on LinkedIn &rarr;</a>}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="font-semibold">Publishing failed</p>
                      <p className="text-xs">{linkedInResult.error}</p>
                      {linkedInResult.error?.includes('CORS') && (
                        <div className="mt-2">
                          <button onClick={handleCopyPost} className="px-3 py-1.5 bg-blue-700 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold">
                            {liCopied ? 'Copied!' : 'Copy formatted post instead'}
                          </button>
                          <p className="text-[10px] text-slate-500 mt-1">Then paste it at linkedin.com/post/new</p>
                        </div>
                      )}
                    </div>
                  )}
                  <button onClick={() => setLinkedInResult(null)} className="mt-2 text-xs opacity-60 underline">Dismiss</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Distribution Panel */}
        <div className="bg-[#0d0f1c] rounded-xl border border-slate-800/60 shadow-lg p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Platform Distribution</h2>
          <DistributionPanel
            platforms={platforms as any}
            isApproved={isApproved}
            onDistribute={triggerDistribution}
            onDistributeAll={triggerAllDistributions}
          />
        </div>

        {/* Generated Content Summary — Real data from pipeline */}
        {getLatestApprovedBundle() && (() => {
          const b = getLatestApprovedBundle()!;
          const infImg = getInfographicImageUrl();
          const comicImg = getComicImageUrl();
          return (
            <div className="bg-[#0d0f1c] rounded-xl border border-slate-800/60 shadow-lg p-6">
              <h2 className="text-2xl font-bold text-white mb-1">Generated Content Summary</h2>
              <p className="text-sm text-slate-400 mb-6">Assets from your pipeline — generated and ready to distribute.</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Blog Post */}
                <div className="border border-slate-700/60 rounded-xl p-4 space-y-2 hover:border-violet-500/30 transition-all bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-lg">📝</span>
                    <span className="font-semibold text-slate-200 text-sm">Blog Post</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400">Ready</span>
                  </div>
                  <p className="text-white font-medium text-sm line-clamp-2">{b.blogPost?.title || b.trendName}</p>
                  <p className="text-slate-400 text-xs line-clamp-3">{b.blogPost?.excerpt || 'No excerpt available'}</p>
                  {b.blogPost?.tags && (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {(b.blogPost.tags as string[]).slice(0, 3).map((tag: string, i: number) => (
                        <span key={i} className="text-xs text-slate-500">#{tag}</span>
                      ))}
                    </div>
                  )}
                  {b.blogPost?.wordCount && (
                    <p className="text-xs text-slate-500 pt-1">{b.blogPost.wordCount} words · blog</p>
                  )}
                </div>

                {/* Infographic */}
                <div className="border border-slate-700/60 rounded-xl p-4 space-y-2 hover:border-violet-500/30 transition-all bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-lg">📊</span>
                    <span className="font-semibold text-slate-200 text-sm">Infographic</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold ${infImg ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>{infImg ? 'Generated' : 'Not yet'}</span>
                  </div>
                  {infImg ? (
                    <img src={infImg} alt="Infographic" className="w-full rounded-lg object-contain max-h-28 bg-slate-800 border border-slate-700" />
                  ) : (
                    <div className="w-full h-28 rounded-lg bg-slate-800/50 border border-dashed border-slate-700 flex items-center justify-center">
                      <p className="text-xs text-slate-500 text-center px-3">Generate in Content Pipeline → Infographic tab</p>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">Instagram / Pinterest · 1080×1350</p>
                </div>

                {/* Comic Strip */}
                <div className="border border-slate-700/60 rounded-xl p-4 space-y-2 hover:border-violet-500/30 transition-all bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-lg">🎨</span>
                    <span className="font-semibold text-slate-200 text-sm">Comic Strip</span>
                    <span className={`ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold ${comicImg ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-700 text-slate-500'}`}>{comicImg ? 'Generated' : 'Not yet'}</span>
                  </div>
                  {comicImg ? (
                    <img src={comicImg} alt="Comic Strip" className="w-full rounded-lg object-contain max-h-28 bg-slate-800 border border-slate-700" />
                  ) : (
                    <div className="w-full h-28 rounded-lg bg-slate-800/50 border border-dashed border-slate-700 flex items-center justify-center">
                      <p className="text-xs text-slate-500 text-center px-3">Generate in Content Pipeline → Comic Strip tab</p>
                    </div>
                  )}
                  <p className="text-xs text-slate-500">LinkedIn / blog embed · 16:9</p>
                </div>
              </div>

              {/* Reel Script */}
              {b.reelScript && (
                <div className="mt-4 border border-slate-700/60 rounded-xl p-4 bg-slate-900/40">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center text-lg">🎬</span>
                    <span className="font-semibold text-slate-200 text-sm">Reel Script</span>
                    <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400">Ready</span>
                    <span className="text-xs text-slate-500">TikTok / Instagram / YouTube Shorts</span>
                  </div>
                  <p className="text-slate-400 text-xs line-clamp-3">{typeof b.reelScript === 'string' ? b.reelScript : (b.reelScript as any)?.hook || 'Script ready — open Content Pipeline to view'}</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Placeholder when no bundle */}
        {!getLatestApprovedBundle() && (
          <div className="bg-[#0d0f1c] rounded-xl border border-slate-800/60 shadow-lg p-6">
            <h2 className="text-2xl font-bold text-white mb-2">Generated Content Summary</h2>
            <div className="flex flex-col items-center py-10 gap-3 text-slate-500">
              <svg className="w-12 h-12 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              <p className="text-base font-medium text-slate-400">No content generated yet</p>
              <p className="text-sm text-center max-w-xs">Run the 3-Agent Pipeline in Content Pipeline first to generate your blog post, infographic, and reel script.</p>
            </div>
          </div>
        )}
        {/* ── Omnichannel Publishing Hub ─────────────────────────────────────── */}
        <div className="rounded-2xl border border-emerald-800/40 bg-[#0a0f14] shadow-xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-emerald-900/60 to-cyan-900/60 border-b border-emerald-700/30 flex items-center gap-4">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-white text-lg">Omnichannel Publishing Hub</h2>
              <p className="text-emerald-300 text-sm">Configure and dispatch to X, LinkedIn, WordPress, Meta, YouTube & MGB in one click.</p>
            </div>
            <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">Multi-Platform Broker</span>
          </div>

          <div className="p-6 space-y-5">
            {/* Platform Matrix */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {([
                { id: 'X' as PlatformId,         label: 'X (Twitter)',      icon: 'X',  color: 'border-slate-600/50', badge: 'POST /2/tweets',         fields: [
                  { key:'token', label:'Bearer Token', type:'password', placeholder:'AAAAAAAAAAAAAAAAAAA…', tooltip: 'Get from developer.twitter.com → Your App → Keys & Tokens → Bearer Token' }
                ] },
                { id: 'LinkedIn' as PlatformId,  label: 'LinkedIn',         icon: 'in', color: 'border-blue-600/50',  badge: 'UGC /ugcPosts',          fields: [
                  { key:'token', label:'Access Token', type:'password', placeholder:'AQV...', tooltip: 'Get from linkedin.com/developers → Your App → Auth → OAuth 2.0 Tokens → Access Token' },
                  { key:'linkedInUrn', label:'Author URN', type:'text', placeholder:'urn:li:person:XXXXX', tooltip: 'Your LinkedIn member URN. Find via GET https://api.linkedin.com/v2/me after OAuth' }
                ] },
                { id: 'WordPress' as PlatformId, label: 'WordPress',        icon: 'W',  color: 'border-indigo-600/50', badge: '/wp-json/wp/v2/posts',  fields: [
                  { key:'wpBaseUrl', label:'WP Base URL', type:'text', placeholder:'https://myblog.com', tooltip: 'Your WordPress site root URL — no trailing slash' },
                  { key:'token', label:'App Password', type:'password', placeholder:'xxxx xxxx xxxx xxxx', tooltip: 'WP Admin → Users → Your Profile → Application Passwords → Add New' }
                ] },
                { id: 'Instagram' as PlatformId, label: 'Instagram',        icon: 'IG', color: 'border-pink-600/50',  badge: 'Meta Graph /media',      fields: [
                  { key:'token', label:'Page Access Token', type:'password', placeholder:'EAABwz…', tooltip: 'Meta Business Suite → Settings → Advanced → System Users → Generate Token with instagram_content_publish permission' },
                  { key:'igUserId', label:'IG Business User ID', type:'text', placeholder:'17841400000000', tooltip: 'GET /me?fields=instagram_business_account after linking your IG account to a FB Page' }
                ] },
                { id: 'Facebook' as PlatformId,  label: 'Facebook',         icon: 'f',  color: 'border-blue-700/50',  badge: 'Graph /page/feed',       fields: [
                  { key:'token', label:'Page Access Token', type:'password', placeholder:'EAABwz…', tooltip: 'Graph API Explorer → Select your Page → Generate Token with pages_manage_posts permission' },
                  { key:'pageId', label:'Page ID', type:'text', placeholder:'123456789', tooltip: 'Found in your Facebook Page → About → Page Transparency → Page ID' }
                ] },
                { id: 'YouTube' as PlatformId,   label: 'YouTube',          icon: 'YT', color: 'border-red-600/50',   badge: 'Data v3 /videos:insert', fields: [
                  { key:'token', label:'OAuth Access Token', type:'password', placeholder:'ya29.a0AfB…', tooltip: 'Google Cloud Console → Credentials → OAuth 2.0 → Authorize with youtube.upload scope' },
                  { key:'ytChannelId', label:'Channel ID', type:'text', placeholder:'UCxxxxxx', tooltip: 'YouTube Studio → Customisation → Basic Info → Channel URL contains your Channel ID' }
                ] },
                { id: 'MGB' as PlatformId,       label: 'MGB Enterprise',   icon: 'MB', color: 'border-amber-600/50', badge: 'Strict JSON /api/posts',  fields: [
                  { key:'mgbBaseUrl', label:'MGB Base URL', type:'text', placeholder:'https://blog.enterprise.gov', tooltip: 'Your MGB API base endpoint — provided by your enterprise admin' },
                  { key:'mgbAuthToken', label:'Compliance Token', type:'password', placeholder:'Bearer …', tooltip: 'Enterprise Bearer token issued by your MGB security team' }
                ] },
              ] as const).map(({ id, label, icon, color, badge, fields }) => {
                const cfg = platformConfigs[id];
                return (
                  <div key={id} className={`rounded-xl border ${color} bg-slate-900/50 p-4 space-y-3 transition-all ${cfg.enabled ? 'ring-1 ring-emerald-500/30' : 'opacity-70'}`}>
                    {/* Platform header */}
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center text-base font-bold text-white flex-shrink-0">{icon}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-white">{label}</p>
                        <p className="text-[10px] text-slate-500 font-mono truncate">{badge}</p>
                      </div>
                      {/* Enable toggle */}
                      <button
                        onClick={() => updatePlatformConfig(id, 'enabled', !cfg.enabled)}
                        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
                          cfg.enabled ? 'bg-emerald-500' : 'bg-slate-700'
                        }`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                          cfg.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                    {/* Config fields (shown when enabled) */}
                    {cfg.enabled && (
                      <div className="space-y-2.5 pt-1">
                        {fields.map(f => (
                          <div key={f.key}>
                            <label className="block text-[10px] font-semibold text-slate-400 mb-1">{f.label}</label>
                            <input
                              type={f.type}
                              placeholder={f.placeholder}
                              value={((cfg as unknown) as Record<string,string>)[f.key] ?? ''}
                              onChange={e => updatePlatformConfig(id, f.key, e.target.value)}
                              className="w-full bg-slate-800/80 border border-slate-700/60 rounded-lg px-2.5 py-2 text-xs text-white placeholder:text-slate-600 focus:border-emerald-500/50 focus:outline-none"
                            />
                            {'tooltip' in f && f.tooltip && (
                              <p className="mt-1 text-[10px] text-slate-600 leading-relaxed">
                                <span className="text-slate-700">&#9432;</span> {f.tooltip}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Status badge from last publish */}
                    {publishResults.find(r => r.platform === id) && (() => {
                      const r = publishResults.find(r => r.platform === id)!;
                      return (
                        <div className={`text-[10px] font-mono px-2 py-1 rounded-lg truncate ${
                          r.success ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                        }`}>{r.message}</div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Dispatch button + logs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handlePublishAll}
                disabled={publishingAll}
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold text-sm transition-all shadow-lg shadow-emerald-500/20"
              >
                {publishingAll ? (
                  <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Dispatching…</>
                ) : (
                  <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg> Publish to All Enabled Platforms</>
                )}
              </button>
              <div className="flex-1 bg-slate-900/60 border border-slate-700/40 rounded-xl p-3 max-h-32 overflow-y-auto space-y-1">
                {pubLogs.length === 0 ? (
                  <p className="text-[11px] text-slate-600 italic">Dispatch log will appear here…</p>
                ) : pubLogs.map((l,i) => (
                  <div key={i} className={`flex items-start gap-2 text-[11px] font-mono ${
                    l.level==='success' ? 'text-emerald-400' : l.level==='error' ? 'text-red-400' : 'text-slate-400'
                  }`}>
                    <span className="text-slate-600 flex-shrink-0">{l.ts}</span>
                    <span>{l.msg}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Headless CMS API Delivery ──────────────────────────────────────── */}
        <div className="rounded-2xl border border-violet-800/40 bg-[#0d0f1c] shadow-xl overflow-hidden">
          {/* Panel header */}
          <div className="px-6 py-4 bg-gradient-to-r from-violet-900/60 to-indigo-900/60 border-b border-violet-700/30 flex items-center gap-4">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m-8-8h16" /></svg>
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-white text-lg">Headless CMS API Delivery</h2>
              <p className="text-violet-300 text-sm">Push Structured JSON directly to your CMS via REST API — no file downloads, no XML.</p>
            </div>
            <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-violet-500/20 text-violet-300 border border-violet-500/30">Content-as-an-API</span>
          </div>

          <div className="p-6 space-y-6">
            {/* CMS Platform tabs */}
            <div className="flex gap-1 p-1 bg-slate-900/60 rounded-xl w-fit border border-slate-700/40">
              {(['strapi', 'sanity', 'contentful'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setCmsTab(t)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all capitalize ${
                    cmsTab === t
                      ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/20'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  {t === 'strapi' ? 'Strapi' : t === 'sanity' ? 'Sanity.io' : 'Contentful'}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Config fields */}
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">API Configuration</p>

                {cmsTab === 'strapi' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">API Base URL</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="https://your-strapi.com/api" value={cmsConfig.strapi.baseUrl} onChange={e => updateCmsCfg('baseUrl', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Strapi Admin → Settings → API Tokens → create Read/Write token. Base URL is your Strapi instance root (e.g. https://cms.yourdomain.com/api)</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Bearer Content Token</label>
                      <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cmsConfig.strapi.token} onChange={e => updateCmsCfg('token', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Strapi Admin → Settings → API Tokens → Add New → Full access → copy token</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Collection Slug</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="articles" value={cmsConfig.strapi.collection} onChange={e => updateCmsCfg('collection', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; The API ID (plural) of your collection type — found in Content-Type Builder</p>
                    </div>
                  </>
                )}
                {cmsTab === 'sanity' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Project ID</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="abc12def" value={cmsConfig.sanity.projectId} onChange={e => updateCmsCfg('projectId', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; sanity.io/manage → your project → Settings → Project ID</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Dataset</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="production" value={cmsConfig.sanity.dataset} onChange={e => updateCmsCfg('dataset', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Usually &quot;production&quot; — found in sanity.io/manage → your project → Datasets</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">API Token</label>
                      <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cmsConfig.sanity.token} onChange={e => updateCmsCfg('token', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; sanity.io/manage → your project → API → Tokens → Add API token → Editor or higher</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Document Type</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="article" value={cmsConfig.sanity.docType} onChange={e => updateCmsCfg('docType', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; The _type field in your schema (e.g. &quot;article&quot;, &quot;post&quot;, &quot;blogEntry&quot;)</p>
                    </div>
                  </>
                )}
                {cmsTab === 'contentful' && (
                  <>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Space ID</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cmsConfig.contentful.spaceId} onChange={e => updateCmsCfg('spaceId', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Contentful → Settings → General Settings → Space ID</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Environment</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="master" value={cmsConfig.contentful.env} onChange={e => updateCmsCfg('env', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Usually &quot;master&quot; — Contentful → Settings → Environments</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Content Management API Token</label>
                      <input type="password" className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" value={cmsConfig.contentful.token} onChange={e => updateCmsCfg('token', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Contentful → Settings → API Keys → Content Management Tokens → Generate personal token</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-400 mb-1.5">Content Type ID</label>
                      <input className="w-full bg-slate-800/60 border border-slate-700/60 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-violet-500/50 focus:outline-none" placeholder="article" value={cmsConfig.contentful.contentTypeId} onChange={e => updateCmsCfg('contentTypeId', e.target.value)} />
                      <p className="mt-1 text-[10px] text-slate-600">&#9432; Contentful → Content model → your type → Content type ID in the sidebar</p>
                    </div>
                  </>
                )}

                {/* Publish button */}
                <button
                  onClick={handleCmsPublish}
                  disabled={cmsPublishing}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white font-semibold text-sm transition-all shadow-lg shadow-violet-500/20"
                >
                  {cmsPublishing ? (
                    <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Publishing…</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg> Push to {cmsTab === 'strapi' ? 'Strapi' : cmsTab === 'sanity' ? 'Sanity.io' : 'Contentful'}</>
                  )}
                </button>
              </div>

              {/* Right: JSON preview + delivery logs */}
              <div className="space-y-4">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Structured JSON Payload Preview</p>
                <pre className="text-[11px] text-emerald-300 font-mono whitespace-pre-wrap leading-relaxed bg-slate-900/80 rounded-xl p-4 border border-slate-700/40 max-h-52 overflow-y-auto">
                  {JSON.stringify(buildStructuredPayload(), null, 2)}
                </pre>

                {/* Delivery logs */}
                <div className="space-y-2">
                  <p className="text-xs font-bold uppercase tracking-widest text-slate-500">Delivery Log</p>
                  {cmsLogs.length === 0 ? (
                    <p className="text-[11px] text-slate-600 italic">No deliveries yet — configure CMS settings and click Push.</p>
                  ) : (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {cmsLogs.map((l, i) => (
                        <div key={i} className={`flex items-start gap-2 text-[11px] font-mono px-3 py-1.5 rounded-lg ${
                          l.level === 'success' ? 'bg-emerald-500/10 text-emerald-300' :
                          l.level === 'error'   ? 'bg-rose-500/10 text-rose-300' :
                                                  'bg-slate-800/40 text-slate-400'
                        }`}>
                          <span className="text-slate-600 flex-shrink-0">{l.ts}</span>
                          <span>{l.msg}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
};

export default Distribution;